import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { X, MapPin, ChevronRight, Lock } from 'lucide-react';
import ShareMenu from '../ShareMenu';
import { PUBLIC_WEB_URL } from '../../config/api';
import './FixtureFinder.css';

/**
 * FixtureFinder — "Clubs near you" (B-43.1). Opened from the hub's Fixtures
 * header ("Find a game") by club admins, team managers and coaches.
 *
 * Read-only: other VERIFIED clubs of the same sport within 10 / 25 / 50 miles
 * of our home ground, nearest first, capped at 50. Each row is name, town and
 * distance, linking to that club's public page — the server never sends more
 * (GET /fixture-finder/nearby). Everything the list leaves out is said on
 * screen (no silent filters): other sports, unverified clubs, clubs that
 * opted out, and the 50 cap.
 *
 * Nobody nearby → "Invite a club you play": the existing Share menu
 * (WhatsApp / copy / system share) with the public marketing URL, built on
 * PUBLIC_WEB_URL so it works from the native app (F-010).
 */
const RADII = [10, 25, 50];

const inviteText = (clubName) => [
  `${clubName ? `${clubName} uses` : 'We use'} *Marakin* to run fixtures, availability and club news in one place.`,
  'Join us on it and we can find each other a game 👇',
  `${PUBLIC_WEB_URL}/`,
].join('\n');

export default function FixtureFinder({ clubSlug, clubName, sport, isClubAdmin, onClose }) {
  const [miles, setMiles] = useState(25);
  // WT-6: `no_home_ground` means "no map pin". A club can still have a ground
  // NAME without one (the Testvale seed; older clubs typed it in), and Settings
  // shows that name, so asking them to "add" it read as a bug. The club record
  // (same cached query as the hub and Club Admin, so no extra request) already
  // carries the name: no API change is needed to tell the two apart.
  const { data: club, isError: clubError } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data),
  });
  // WT-17 (16 Oct walk-through): the same club record says whether we're
  // verified (`verified_at`) and whether the details have been sent
  // (`verification_submitted_at`, both on the public GET /clubs/:slug). An
  // unverified club no longer asks the server, whose 403 club_not_verified
  // was left in the console. The server's 403 still counts, in case the
  // cached record is out of date.
  const knownUnverified = !!club && !club.verified_at;
  const { data, isLoading, error } = useQuery({
    queryKey: ['fixture-finder', clubSlug, miles],
    queryFn: () => axios.get(`/clubs/${clubSlug}/fixture-finder/nearby`, { params: { miles } }).then(r => r.data),
    enabled: (!!club || clubError) && !knownUnverified,
    retry: false,
    staleTime: 60 * 1000,
  });
  const notVerified = knownUnverified || error?.response?.data?.error === 'club_not_verified';
  const detailsSent = !!club?.verification_submitted_at;
  const clubs = data?.clubs || [];
  const groundName = (club?.home_ground_name || '').trim();
  const groundUnpinned = data?.status === 'no_home_ground' && !!groundName;
  // WT-6: the distance pills can't change anything until we can search.
  const cantSearch = data?.status === 'no_home_ground' || data?.status === 'no_sport';
  // The sport the server matched on (canonical name, e.g. "Rugby Union") —
  // shown in the title so a mismatched club sport is visible, not silent.
  const sportLabel = data?.sport || sport || null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card modal-md ff-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{sportLabel ? `${sportLabel} clubs near you` : 'Clubs near you'}</h3>
          <button className="btn btn-ghost btn-icon" aria-label="Close" onClick={onClose}><X size={18} /></button>
        </div>

        {notVerified ? (
          // WT-17: details already sent → say we're on it; otherwise ask for them.
          <div className="ff-empty">
            <Lock size={22} />
            {detailsSent ? (
              <>
                <p><strong>We're checking your club's details.</strong></p>
                <p>Find a game opens once your club is verified.</p>
              </>
            ) : (
              <>
                <p><strong>Find a game opens once Marakin verifies your club.</strong></p>
                {isClubAdmin
                  ? <p><Link to={`/${clubSlug}/admin?tab=sponsors`} onClick={onClose}>Send your club's verification details</Link> to get started.</p>
                  : <p>Your club admin can send the verification details from Club Admin.</p>}
              </>
            )}
          </div>
        ) : (
          <>
            <p className="modal-subtitle">
              Other {sportLabel ? <strong>{sportLabel}</strong> : 'same-sport'} clubs on Marakin near your home ground. Only clubs
              Marakin has verified are listed, and a club can choose not to be.
            </p>
            {!cantSearch && (
              <div className="ff-radius" role="group" aria-label="Distance">
                {RADII.map(m => (
                  <button key={m} type="button"
                    className={`fixture-view-btn ${miles === m ? 'active' : ''}`}
                    aria-pressed={miles === m}
                    onClick={() => setMiles(m)}>
                    {m} miles
                  </button>
                ))}
              </div>
            )}

            {(isLoading || (!club && !clubError)) && <div className="page-loading" style={{ minHeight: 100 }}><div className="spinner" /></div>}
            {error && !notVerified && (
              <div className="ff-note is-error">Couldn't load nearby clubs. Please try again.</div>
            )}

            {groundUnpinned && (
              <div className="ff-empty">
                <MapPin size={22} />
                <p>We can't place <strong>{groundName}</strong> on the map yet.</p>
                {isClubAdmin
                  ? <p>In <Link to={`/${clubSlug}/admin?tab=settings`} onClick={onClose}>Club Admin → Settings</Link>, search for your ground and pick a suggestion so we can find clubs near you.</p>
                  : <p>Ask your club admin to search for your ground in Club Admin → Settings and pick a suggestion, so we can find clubs near you.</p>}
              </div>
            )}
            {data?.status === 'no_home_ground' && !groundUnpinned && (
              <div className="ff-empty">
                <MapPin size={22} />
                <p>We need your club's home ground to work out who's nearby.</p>
                {isClubAdmin
                  ? <p><Link to={`/${clubSlug}/admin?tab=settings`} onClick={onClose}>Add it in Club Admin → Settings</Link>.</p>
                  : <p>Ask your club admin to add it in Club Admin → Settings.</p>}
              </div>
            )}
            {data?.status === 'no_sport' && (
              <div className="ff-empty">
                <p>Your club's sport isn't set, so we can't match you with other clubs.</p>
                {isClubAdmin
                  ? <p><Link to={`/${clubSlug}/admin?tab=settings`} onClick={onClose}>Set it in Club Admin → Settings</Link>.</p>
                  : <p>Ask your club admin to set it in Club Admin → Settings.</p>}
              </div>
            )}

            {data?.status === 'ok' && clubs.length > 0 && (
              <ul className="ff-list">
                {clubs.map(c => (
                  <li key={c.slug}>
                    <Link to={`/${c.slug}`} className="ff-row" onClick={onClose}>
                      <span className="ff-main">
                        <span className="ff-name">{c.name}</span>
                        {c.town && <span className="ff-town">{c.town}</span>}
                      </span>
                      <span className="ff-dist">
                        {c.distance_miles < 1 ? 'under 1 mile' : `${c.distance_miles} mile${c.distance_miles === 1 ? '' : 's'}`}
                      </span>
                      <ChevronRight size={16} className="ff-chev" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {data?.status === 'ok' && data.capped && (
              <p className="ff-foot">Showing the nearest {data.limit}. Choose a shorter distance to narrow it down.</p>
            )}

            {data?.status === 'ok' && clubs.length === 0 && (
              <div className="ff-empty">
                <p>No other {sportLabel || 'same-sport'} clubs on Marakin within {data.miles} miles yet.</p>
                {data.miles < 50 && <p>Try a longer distance, or invite a club you play.</p>}
              </div>
            )}

            {data?.status === 'ok' && (
              <div className="ff-invite">
                <span>{clubs.length ? "Can't see a club you play?" : 'Invite a club you play'}</span>
                <ShareMenu text={inviteText(clubName)} clubSlug={clubSlug} kind="club"
                  label="Invite a club you play" align="right" />
              </div>
            )}

            {data?.hidden && (
              <p className="ff-foot">
                Your club is hidden from other clubs' lists.
                {isClubAdmin && <> Change this in <Link to={`/${clubSlug}/admin?tab=settings`} onClick={onClose}>Club Admin → Settings</Link>.</>}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
